import React from 'react';
import Bugsnag from '@bugsnag/js';
import ConfirmationModal from '@99designs/apps/spa/common/ConfirmationModal';
import Visible from '@99designs/apps/spa/pages/create/common/components/Visible';
import { DesignerCard_UserFragment } from '@99designs/apps/spa/pages/create/components/DesignerCard.generated';
import { trackRemovedDesignerEvent } from '@99designs/apps/spa/pages/create/events';
import RemoveStructuredBriefDesigners from '@99designs/apps/spa/pages/create/mutations/RemoveStructuredBriefDesigners';
import { DesignerMetaHeader } from '@99designs/designers';
import { Box, Button, Inline } from '@99designs/emu';
import { WarningStatusMessage } from '@99designs/frontend-components/components/StatusMessage/StatusMessage';

interface Props {
    brief: {
        id: string;
        designers: DesignerCard_UserFragment[];
    };
    onConfirm: () => void;
    onDismiss: () => void;
    saving?: boolean;
    showing: boolean;
}

function DesignerUnavailableMessage() {
    return (
        <WarningStatusMessage>
            <>
                {__(
                    'Oops! One or more of the designers you have selected is currently not available to work with.'
                )}
                <br />
                {__('Please either remove them or try again later.')}
            </>
        </WarningStatusMessage>
    );
}

function DesignerConfirmationModal({
    brief,
    onConfirm,
    onDismiss,
    saving = false,
    showing,
}: Props) {
    const heading =
        brief.designers.length > 1 ? __('Confirm your designers') : __('Confirm your designer');

    const unavailableDesigners = brief.designers.filter((d) => !d.profile.isAvailable);

    return (
        <ConfirmationModal onDismiss={onDismiss} heading={heading} showing={showing}>
            <RemoveStructuredBriefDesigners>
                {(removeDesigners) => (
                    <div className="designer-meta-list">
                        {brief.designers.map((designer) => (
                            <div
                                className="designer-meta-list__item"
                                key={designer.id}
                                data-testid="designer-meta-list-item"
                            >
                                <DesignerMetaHeader
                                    designer={designer}
                                    onRemove={(id) => {
                                        trackRemovedDesignerEvent({
                                            id: brief.id,
                                            designer_id: parseInt(designer.id),
                                        });

                                        removeDesigners({
                                            id: brief.id,
                                            designerIds: [id],
                                        }).catch(Bugsnag.notify);
                                    }}
                                    paragraphSize={'regular'}
                                    avatarSize={'small'}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </RemoveStructuredBriefDesigners>
            {unavailableDesigners.length > 0 && <DesignerUnavailableMessage />}
            <div style={{ textAlign: 'right' }}>
                <Inline space="0.75bu">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            return onDismiss();
                        }}
                        className="link link--back link--big-target"
                    >
                        {__('Back')}
                    </a>

                    <Box color="grey800">
                        <Visible sizes={['small', 'medium']}>
                            <Button
                                onClick={onConfirm}
                                appearance="primary"
                                loading={saving}
                                disabled={unavailableDesigners.length > 0}
                            >
                                {__('Confirm')}
                            </Button>
                        </Visible>
                        <Visible sizes={['normal', 'large']}>
                            <Button
                                onClick={onConfirm}
                                appearance="primary"
                                size="large"
                                loading={saving}
                                disabled={unavailableDesigners.length > 0}
                                data-testid="confirm-button-large-screen"
                            >
                                {__('Confirm')}
                            </Button>
                        </Visible>
                    </Box>
                </Inline>
            </div>
        </ConfirmationModal>
    );
}

export default DesignerConfirmationModal;
